import { csrf_headers, get_token, set_user_details, getCookie, set_drive_id } from '@/router/index.js';
import $ from 'jquery';
import Swal from 'sweetalert2';
import { get_domain_style } from '@/main';
import { initialize_push_notifications } from '@/services/push_notification_service';
import OneChatWebSocketClient from '@/services/one_chat_websocket';

export default {
  name: 'NavbarView',

  data: () => ({
    user: {},
    form: {},
    original_user: null,
    original_company: null,
    original_profile: null,
    saving: false,
    user_profile: {
      phone_number: '',
      address: '',
      id_number: '',
      province: '',
      country: '',
      gender: ''
    },
    new_company: {
      company_name: '',
      registration_number: '',
      vat_number: '',
      phone_number: '',
      address: ''
    },
    user_initials: '',
    edit_mode: false,
    email_touched: false,
    email_error: '',
    registration_error: '',
    vat_error: '',
    id_number_error: null,
    search_query: '',
    show_panel: false,
    is_searching: false,
    results: [],
    total_count: 0,
    limit: 10,
    offset: 0,
    debounce_id: null,
    search_modal: null,
    _search_modal_visible: false,
    logo_url: null,

    // NOTIFICATION & MESSAGES PROPERTIES
    notifications: [],
    unread_count: 0,
    loading_notifications: false,
    notification_filter: 'all',
    notification_poll_interval: null,
    last_notification_fetch: null,
    unread_chat_count: 0,
    chat_socket: null,
    chat_socket_connected: false,
    chat_socket_last_error: null,

    // BROWSER NOTIFICATION FLAGS
    _notifications_initialized: false,
    _chat_count_initialized: false,
  }),

  computed: {
    styleConfig() {
      return get_domain_style();
    },
    is_email_valid() {
      const email = (this.user?.email || '').trim();
      if (!email) return false;
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      return re.test(email);
    },
    grouped_results() {
      const allowed_types = new Set((this.is_admin() ? 'user,profile,company,audit,other' : 'profile,company,audit,other').split(','));
      const groups_map = new Map();

      const to_safe_string = (v) => {
        const t = typeof v;
        if (t === 'string')  return v;
        if (t === 'number')  return String(v);
        if (t === 'boolean') return String(v);
        return '';
      };

      const safeGet = (obj, key) => {
        const d = Object.getOwnPropertyDescriptor(obj, key);
        return d && d.enumerable ? to_safe_string(d.value) : '';
      };
      const visible = this.is_admin() ? this.results : this.results.filter(r => (r?.type || '').toLowerCase() !== 'user')

      for (const r of visible) {
        let t = String((r && r.type) || 'other').toLowerCase();
        if (!allowed_types.has(t)) t = 'other';

        // eslint-disable-next-line no-array-constructor
        if (!groups_map.has(t)) groups_map.set(t, []);

        const clean = {
          id: safeGet(r, 'id'),
          type: safeGet(r, 'type'),
          title: safeGet(r, 'title'),
          subtitle: safeGet(r, 'subtitle'),
        };

        groups_map.get(t).push(clean);
      }

      return Object.fromEntries(groups_map);
    },

    filtered_notifications() {
      if (this.notification_filter === 'unread') {
        return this.notifications.filter(n => n.status !== 'READ');
      }
      return this.notifications;
    }
  },

  mounted() {
    this.csrfToken = getCookie("csrftoken");
    this.get_user_details().then(() => {
      if (this.user.company_id) {
        this.get_company_profile(this.user.company_id);
      }
      else if (this.user.profile_id) {
        this.get_user_profile(this.user.profile_id);
      }
    });
    this.get_drive_id();
    this.init_branding();

    // INITIALIZE NOTIFICATIONS AND MESSAGES
    this.fetch_notifications();
    this.fetch_unread_count();
    this.init_chat_socket();
    this.start_notification_polling();
     this.request_notification_permission();

    // Listen for the instant refresh event from OneChatView
    window.addEventListener('refresh-unread-chats', this.fetch_unread_chat_count);
  },

  beforeUnmount() {
    if (this.notification_poll_interval) {
      clearInterval(this.notification_poll_interval);
    }
    if (this.chat_socket) this.chat_socket.disconnect();
    window.removeEventListener('refresh-unread-chats', this.fetch_unread_chat_count);
  },

  methods: {

    async request_notification_permission() {
      initialize_push_notifications();
      if (process.env.VUE_APP_PUSH_LEGACY_NAV_PROMPT !== 'true') return;
      if (!('Notification' in window)) {
        console.warn('This browser does not support desktop notifications.');
        return;
      }
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          // Fire a welcome notification so the user sees it's working
          this.fire_browser_notification(
            'Notifications enabled — AIC One',
            "You'll be notified of new messages and alerts even when this tab is in the background.",
            { tag: 'aic-welcome', icon: this.get_navbar_logo_src() }
          );
        }
      }
    },

    fire_browser_notification(title, body, options = {}) {
      if (!('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;

      const notif = new Notification(title, {
        body,
        icon: options.icon || this.get_navbar_logo_src(),
        badge: options.icon || this.get_navbar_logo_src(),
        tag: options.tag || 'aic-notification',
        requireInteraction: false,
        silent: false,
      });

      notif.onclick = () => {
        window.focus();
        if (typeof options.on_click === 'function') {
          options.on_click();
        }
        notif.close();
      };

      setTimeout(() => notif.close(), 9000);
    },

    is_admin() {
      const v = localStorage.getItem('is_admin');
      return v === '1' || v === 'true' || v === true || v === 'True';
    },

    get_own_data_descriptor(obj, key) {
      const d = Object.getOwnPropertyDescriptor(obj ?? {}, key);
      return d && d.enumerable && !('get' in d) && !('set' in d) ? d : null;
    },

    coerce_to_safe_string(v) {
      const COERCERS = { string: (x) => x, number: String, boolean: String };
      const fn = COERCERS[typeof v];
      return fn ? fn(v) : '';
    },

    safe_own_string(obj, key) {
      const d = this.get_own_data_descriptor(obj, key);
      return d ? this.coerce_to_safe_string(d.value) : '';
    },

    normalized_diff(a, b) {
      return this.normalize(a) !== this.normalize(b);
    },

    only_digits(event) {
      const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'];
      if (allowedKeys.includes(event.key)) return;
      if (!/[0-9/]/.test(event.key)) {
        event.preventDefault();
      }
    },

    open_search_modal_from_nav() {
      this.open_search_modal();
      this.$nextTick(() => {
        const modal_el = this.$refs.global_search_modal;
        if (!modal_el) return;
        const modal_input = modal_el.querySelector('input[type="search"]');
        if (modal_input) {
          modal_input.focus();
          modal_input.select();
        }
      });
    },

    validate_registration() {
      if (this.new_company.registration_number.length > 14) {
        this.new_company.registration_number = this.new_company.registration_number.slice(0, 14);
      }
      const reg_pattern = /^\d{4}\/\d{6}\/\d{2}$/;
      if (!reg_pattern.test(this.new_company.registration_number)) {
        this.registration_error = 'Please enter a valid registration number';
      } else {
        this.registration_error = '';
      }
    },

    on_registration_input(e) {
      const raw = e?.target?.value ?? '';
      const formatted = this.format_registration_number(raw);
      this.new_company.registration_number = formatted;
      this.validate_registration();
    },

    format_registration_number(value) {
      const digits = String(value).replace(/\D/g, '').slice(0, 12);
      const year = digits.slice(0, 4);
      const number_1 = digits.slice(4, 10);
      const number_2 = digits.slice(10, 12);
      let out = year;
      if (number_1) out += `/${number_1}`;
      if (number_2) out += `/${number_2}`;
      return out || '';
    },

    validate_vat() {
      if (this.new_company.vat_number.length > 10) {
        this.new_company.vat_number = this.new_company.vat_number.slice(0, 10);
      }
      const vat_pattern = /^\d{10}$/;
      if (!vat_pattern.test(this.new_company.vat_number)) {
        this.vat_error = 'Please enter a valid VAT number (10 digits)';
      } else {
        this.vat_error = '';
      }
    },

    on_email_input(e) {
      const value = (e?.target?.value ?? '').trim();
      this.user.email = value.toLowerCase();
      this.email_touched = true;
      this.email_error = this.is_email_valid ? '' : 'Please enter a valid email address.';
    },

    validate_before_save() {
      if (!this.is_email_valid) {
        this.email_touched = true;
        this.email_error = 'Please enter a valid email address.';
        this.$notyf.info('Invalid email. Please fix and try again.');
        return false;
      }
      return true;
    },

  validate_sa_id_number(id_number) {
    const id_regex = /^\d{13}$/;
    if (!id_regex.test(id_number)) return false;

    const yy = id_number.substring(0, 2);
    const mm = id_number.substring(2, 4);
    const dd = id_number.substring(4, 6);

    const today = new Date();
    const year = today.getFullYear().toString().substr(-2);
    const century = (parseInt(yy, 10) <= parseInt(year, 10)) ? '20' : '19';
    const full_year = parseInt(century + yy, 10);

    const dob = new Date(full_year, parseInt(mm, 10) - 1, parseInt(dd, 10));
    if (
      dob.getFullYear() !== full_year ||
      dob.getMonth() !== parseInt(mm, 10) - 1 ||
      dob.getDate() !== parseInt(dd, 10)
    ) {
      return false;
    }

    return this.luhn_algorithm(id_number);
  },

  luhn_algorithm(id_number) {
    let sum = 0;
    let is_even = false;
    for (let i = id_number.length - 1; i >= 0; i--) {
      let digit = parseInt(id_number.charAt(i), 10);
      if (is_even) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      is_even = !is_even;
    }
    return (sum % 10 === 0);
  },

  validate_id_number() {
    this.user_profile.id_number = this.user_profile.id_number.replace(/\D/g, '').slice(0, 13);
    if (!this.user_profile.id_number || this.user_profile.id_number.trim() === '') {
      this.id_number_error = 'ID number is required';
    } else if (!this.validate_sa_id_number(this.user_profile.id_number)) {
      this.id_number_error = 'Invalid ID number';
    } else {
      this.id_number_error = null;
    }
  },

  get_drive_id() {
    const url = process.env.VUE_APP_API_BASE_URL + '/aic_one_api/aic_drive/get_drive/';
    const token = get_token();
    if (!token) {
      this.$router.push({ name: 'Login' });
      return;
    }
    const user_id = localStorage.getItem('user_id');
    const payload = { user_id: user_id };
    return new Promise((resolve, reject) => {
      $.ajax({
        url: url,
        method: "GET",
        data: payload,
        headers: {
          "Authorization": `Token ${token}`
        },
        success: (response) => {
          const response_data = typeof response === "string" ? JSON.parse(response) : response;
          const drive_id = response_data.data.drive_id;
          set_drive_id(drive_id);
        },
        error: (xhr, status, error) => {
          console.error("Error fetching drive", xhr.responseText || error);
          reject(error);
        }
      });
    });
  },

  normalize(v) { return (v ?? '').toString().trim(); },

  core_user_changed() {
    if (!this.original_user) return false;
    return (
      this.normalize(this.user.first_name) !== this.normalize(this.original_user.first_name) ||
      this.normalize(this.user.last_name)  !== this.normalize(this.original_user.last_name)  ||
      this.normalize((this.user.email || '').toLowerCase()) !== this.normalize(this.original_user.email)
    );
  },

  company_changed() {
    if (!this.original_company) return true;
    const now = this.new_company || {};
    const orig = this.original_company;
    const fields = ['company_name', 'registration_number', 'vat_number', 'phone_number', 'address'];
    return fields.some(k =>
      this.normalized_diff(
        this.safe_own_string(now, k),
        this.safe_own_string(orig, k)
      )
    );
  },

  profile_changed() {
    if (!this.original_profile) return true;
    const now = this.user_profile || {};
    const orig = this.original_profile;
    const fields = ['phone_number','address','id_number','province','country','gender'];
    return fields.some(k =>
      this.normalized_diff(
        this.safe_own_string(now, k),
        this.safe_own_string(orig, k)
      )
    );
  },

  parse_backend_error(err) {
    if (!err) return "An unknown error occurred";
    if (typeof err === "string") return err;
    const msg = err.message || err.error || err.detail || err;
    if (typeof msg === "string") return msg;
    if (msg && typeof msg === "object") {
      const keys = Reflect.ownKeys(msg);
      return keys
        .map((key) => {
          const value = Reflect.get(msg, key);
          if (Array.isArray(value)) return `${String(key)}: ${value.join(", ")}`;
          if (typeof value === "string") return `${String(key)}: ${value}`;
          return `${String(key)}: ${JSON.stringify(value)}`;
        })
        .join(" | ");
    }
    return JSON.stringify(msg);
  },

  async update_core_user() {
    const token = get_token();
    if (!token) { this.$router.push({ name: 'Login' }); return false; }
    const payload = {
      first_name: (this.user.first_name || '').trim(),
      last_name: (this.user.last_name  || '').trim(),
      email: (this.user.email || '').trim().toLowerCase(),
    };
    const res = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/update_user/`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
          'X-Page-URL': window.location.href,
          ...csrf_headers(this.csrfToken),
        },
        body: JSON.stringify(payload),
      }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status !== 'success') {
      const friendly_message = this.parse_backend_error(data);
      this.$notyf.error(friendly_message);
      return false;
    }
    if (data.data) { this.user = { ...this.user, ...data.data }; }
    set_user_details(this.user);
    return true;
  },

  async get_user_details() {
    const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_current_user/`;
    const token = get_token();
    if (!token) {
      this.$router.push({ name: 'Login' });
      return;
    }
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
      });
      if (response.ok) {
        const response_data = await response.json();
        this.user = response_data.data;
        this.original_user = {
          first_name: this.user.first_name || '',
          last_name: this.user.last_name || '',
          email: (this.user.email || '').toLowerCase(),
        }
        set_user_details(this.user);
        if (this.user && this.user.first_name && this.user.last_name) {
          this.user_initials = this.user.first_name[0].toUpperCase() + this.user.last_name[0].toUpperCase();
        }
         this.init_branding();
      } else {
           if (response.status === 403 || response.status === 401) {
                // Redirect to login on auth errors
                this.$router.push({ name: 'Login' });
            } else {
                this.$notyf.info('Error fetching user details');
            }
        }
      } catch (error) {
        this.$notyf.info('Error fetching user details');
      }
  },

  async create_user_profile() {
    const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/create_user_profile/`;
    const token = get_token();
    if (!token) {
      this.$router.push({ name: 'Login' });
      return;
    }
    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
          ...csrf_headers(this.csrfToken)
        },
        body: JSON.stringify(this.user_profile),
      });
      if (response.ok) {
        const data = await response.json();
        this.user_profile = data.data;
        this.$notyf.success('User profile created successfully');
        return true;
      } else {
        this.$notyf.error('Error creating profile');
        return false;
      }
    } catch (error) {
      this.$notyf.error('Error creating profile');
    }
  },

    async create_company_profile() {
      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/create_client_company/`;
      const token = get_token();
      if (!token) {
        this.$router.push({ name: 'Login' });
        return;
      }
      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            ...csrf_headers(this.csrfToken)
          },
          body: JSON.stringify(this.new_company),
        });
        const data = await response.json();
        if (response.ok) {
          this.new_company = data.data;
          return true;
        } else {
          this.$notyf.error(data.message || 'Error creating company');
          return false;
        }
      } catch (error) {
        this.$notyf.error('Error creating company profile');
      }
    },

    async save_changes() {
      if (this.registration_error || this.vat_error || this.id_number_error) {
        return;
      }
      if (this.saving) return;
      this.saving = true;

      const is_org = (this.user?.client_type || '').toLowerCase() === 'organization';
      const core_changed = this.core_user_changed();
      const org_changed  = is_org ? this.company_changed() : false;
      const prof_changed = !is_org ? this.profile_changed() : false;

      if (!core_changed && !org_changed && !prof_changed) {
        this.$notyf.info('No changes to save.');
        this.edit_mode = false;
        this.saving = false;
        return;
      }

      if (core_changed) {
        if (!this.validate_before_save()) { this.saving = false; return; }
        const core = await this.update_core_user();
        if (!core) { this.saving = false; return; }
        this.original_user = {
          first_name: (this.user.first_name || '').trim(),
          last_name: (this.user.last_name || '').trim(),
          email: (this.user.email || '').trim().toLowerCase(),
        };
      }

      if (org_changed) {
        const r = await this.create_company_profile();
        if (!r) { this.saving = false; return; }
        this.original_company = {
          company_name: this.new_company.company_name || '',
          registration_number: this.new_company.registration_number || '',
          vat_number: this.new_company.vat_number || '',
          phone_number: this.new_company.phone_number || '',
          address: this.new_company.address || '',
        };
      }

      if (prof_changed) {
        const r = await this.create_user_profile();
        if (!r) { this.saving = false; return; }
        this.original_profile = {
          phone_number: this.user_profile.phone_number || '',
          address: this.user_profile.address || '',
          id_number: this.user_profile.id_number || '',
          province: this.user_profile.province || '',
          country: this.user_profile.country || '',
          gender: this.user_profile.gender || '',
        };
      }

      this.edit_mode = false;
      this.$notyf.success('Changes saved successfully');
      this.saving = false;
    },

    async get_company_profile(company_id) {
      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_client_company?company_id=${company_id}`;
      const token = get_token();
      if (!token) {
        this.$router.push({ name: 'Login' });
        return;
      }
      try {
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            ...csrf_headers(this.csrfToken)
          },
        });
        if (response.ok) {
          const response_data = await response.json();
          this.new_company = response_data.data;
          this.original_company = {
            company_name: this.new_company.company_name || '',
            registration_number: this.new_company.registration_number || '',
            vat_number: this.new_company.vat_number || '',
            phone_number: this.new_company.phone_number || '',
            address: this.new_company.address || '',
          };
        } else {
          this.$notyf.info('Error fetching organization details');
        }
      } catch (error) {
        this.$notyf.info('Error fetching organization details');
      }
    },

    async get_user_profile(profile_id) {
      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_user_profile?profile_id=${profile_id}`;
      const token = get_token();
      if (!token) {
        this.$router.push({ name: 'Login' });
        return;
      }
      try {
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            ...csrf_headers(this.csrfToken)
          },
        });
        if (response.ok) {
          const data = await response.json();
          this.user_profile = data.data;
          this.original_profile = {
            phone_number: this.user_profile.phone_number || '',
            address: this.user_profile.address || '',
            id_number: this.user_profile.id_number || '',
            province: this.user_profile.province || '',
            country: this.user_profile.country || '',
            gender: this.user_profile.gender || '',
          };
        } else {
          this.$notyf.info('Error fetching user profile');
        }
      } catch (error) {
        this.$notyf.info('Error fetching user profile');
      }
    },

    on_search_input() {
      this.show_panel = true;
      clearTimeout(this.debounce_id);
      const q = (this.search_query || '').trim();
      if (!q) { this.results = []; this.total_count = 0; return; }
      this.debounce_id = setTimeout(() => this.run_search(q, 0), 250);
    },

    async run_search(query, offset = 0, expand = false) {
      const token = get_token();
      if (!token) { this.$router.push({ name: 'Login' }); return; }
      const limit = expand ? 30 : this.limit;
      const params = new URLSearchParams();
      params.set('search_query', query.trim());
      params.set('include', this.is_admin() ? 'user,profile,company' : 'profile,company');
      params.set('limit', String(limit));
      params.set('offset', String(offset));
      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/global_search/?` + params.toString();
      this.is_searching = true;
      try {
        const res = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Authorization': `Token ${token}`,
            'X-Page-URL': window.location.href,
          },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message || 'Search failed');
        this.results = Array.isArray(data?.results) ? data.results : [];
        this.total_count = Number.isFinite(data?.count) ? data.count : 0;
      } catch (e) {
        this.$notyf?.info?.(e?.message || 'Search failed');
        this.results = [];
        this.total_count = 0;
      } finally {
        this.is_searching = false;
      }
    },

    async checkAndPushRoute(loc) {
      let resolved;
      if (typeof loc === 'string') {
        resolved = this.$router.resolve(loc);
      } else if (loc && typeof loc === 'object' && 'name' in loc) {
        if (!(this.$router.hasRoute && this.$router.hasRoute(loc.name))) return false;
        resolved = this.$router.resolve(loc);
      } else if (loc && typeof loc === 'object') {
        resolved = this.$router.resolve(loc);
      } else {
        return false;
      }
      if (!resolved?.matched?.length) return false;
      if (this.$route.fullPath !== resolved.fullPath) {
        await this.$router.push(resolved);
      }
      return true;
    },

    async open_result(item) {
      const t = (item?.type || '').toLowerCase();
      const id = String(item?.id ?? '');
      const closeUI = () => { this.show_panel = false; this.close_search_modal?.(); };
      const try_push = async (cands) => {
        for (const c of cands) {
           if (await this.checkAndPushRoute(c)) { closeUI(); return true; }
        }
        return false;
      };
      const push_path = try_push;

      if (t === 'user') {
       if (!this.is_admin()) {
        this.$notyf?.info?.('You do not have permission to view user records.');
         return;
       }

       const ok = await try_push([
             { name: 'Settings' },
            `/users/${id}`,
            `/user/${id}`,
             { path: `/settings?focus=user&user_id=${id}` },
        ]);
       if (!ok) this.$notyf?.info?.('No user route found.');
         return;
       }

      if (t === 'profile') {
        const ok = await try_push([{ name: 'Profile' }])
              || await push_path([`/profiles/${id}`, `/profile/${id}`]);
        if (!ok) this.$notyf?.info?.('No profile route found.');
        return;
      }

      if (t === 'company') {
        const ok = await try_push([{ name: 'Profile' }])
              || await push_path([`/companies/${id}`, `/company/${id}`]);
        if (!ok) this.$notyf?.info?.('No company route found.');
        return;
      }

      if (t === 'audit') {
        const ok = await try_push([{ name: 'AuditTrail' }])
              || await push_path([`/audits?focus_id=${encodeURIComponent(id)}`]);
        if (!ok) this.$notyf?.info?.('No audit route found.');
        return;
      }

      this.$notyf?.info?.('No handler for this result type yet.');
    },

    open_search_modal() {
      const el = this.$refs.global_search_modal;
      if (!el) return;
      // eslint-disable-next-line no-undef
      this.search_modal = this.search_modal || new bootstrap.Modal(el, { backdrop: true, keyboard: true });
      this.search_modal.show();
      this._search_modal_visible = true;
      this.$nextTick(() => {
        const input = el.querySelector('input[type="search"]');
        input && input.focus();
        if (input && typeof input.selectionStart === 'number') {
          const len = input.value.length;
          input.setSelectionRange(len, len);
        }
      });
    },

    close_search_modal() {
      this.search_modal?.hide?.();
      this._search_modal_visible = false;
    },

    async init_branding() {
      if (this.$branding) {
        const branding_data = this.$branding.get_branding();
        if (branding_data.logos && branding_data.logos.logo) {
          this.logo_url = branding_data.logos.logo;
        }
        if (!branding_data.initialized && this.user?.company_id) {
          const org_id = this.user.company_id || this.user.organization_id;
          if (org_id) {
            const loaded = await this.$branding.load_branding(org_id);
            if (loaded && loaded.logos && loaded.logos.logo) {
              this.logo_url = loaded.logos.logo;
            }
          }
        }
      }
    },

    get_navbar_logo_src() {
      return this.logo_url || this.styleConfig.logo;
    },

    handle_navbar_logo_error(e) {
      e.target.src = this.styleConfig.logo;
    },

    page_next() {
      if (this.offset + this.limit < this.total_count) {
        this.run_search(this.search_query, this.offset + this.limit);
      }
    },

    page_prev() {
      if (this.offset > 0) {
        this.run_search(this.search_query, Math.max(0, this.offset - this.limit));
      }
    },

    on_nav_input(e) {
      if (!this._search_modal_visible) {
        this.open_search_modal_from_nav();
      }
      this.search_query = (e?.target?.value || '').trimStart();
      this.on_search_input();
    },

    // NOTIFICATION METHODS

    async fetch_notifications(show_loader = true) {
      const token = get_token();
      if (!token) {
        this.$router.push({ name: 'Login' });
        return;
      }

      if (show_loader) {
        this.loading_notifications = true;
      }

      try {
        const params = new URLSearchParams({
          unread_only: 'false',
          include_dismissed: 'false'
        });

        const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/notification_management/get_user_notifications/?${params}`;

        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
            'X-Page-URL': window.location.href,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success') {
            const incoming = data.notifications || [];

            if (this._notifications_initialized) {
              const existing_ids = new Set(this.notifications.map(n => n.id));
              const new_ones = incoming.filter(
                n => !existing_ids.has(n.id) && n.status !== 'READ'
              );

              if (new_ones.length === 1) {
                // Single notification — show its exact message
                this.fire_browser_notification(
                  'New Notification — AIC One',
                  new_ones[0].message,
                  {
                    tag: `notif-${new_ones[0].id}`,
                    on_click: () => {
                      const action_url = new_ones[0].action_url;
                      if (action_url) {
                        try {
                          if (action_url.startsWith('http')) {
                            const url_obj = new URL(action_url);
                            if (url_obj.hostname === window.location.hostname) {
                              this.$router.push(url_obj.pathname + url_obj.search + url_obj.hash);
                            }
                          } else {
                            this.$router.push(action_url);
                          }
                        } catch (e) {
                          this.$router.push(action_url);
                        }
                      }
                    },
                  }
                );
              } else if (new_ones.length > 1) {
                // Multiple — batch them into one OS popup
                this.fire_browser_notification(
                  'New Notifications — AIC One',
                  `You have ${new_ones.length} new notifications`,
                  { tag: 'notif-batch' }
                );
              }
            }

            this.notifications = incoming;
            this._notifications_initialized = true;
          }
        } else if (response.status === 401 || response.status === 403) {
          this.$router.push({ name: 'Login' });
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        if (show_loader) {
          this.loading_notifications = false;
        }
      }
    },

    async fetch_unread_count() {
      const token = get_token();
      if (!token) return;
      try {
        const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/notification_management/get_unread_notification_count/`;
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success') {
            this.unread_count = data.unread_count || 0;
          }
        }
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    },

    async mark_notification_read(notification_id) {
      const token = get_token();
      if (!token) return;
      try {
        const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/notification_management/mark_notification_as_read/`;
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
            'X-Page-URL': window.location.href,
            ...csrf_headers(this.csrfToken),
          },
          body: JSON.stringify({ notification_id }),
        });
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success') {
            const notif = this.notifications.find(n => n.id === notification_id);
            if (notif) {
              notif.status = 'READ';
              notif.read_at = new Date().toISOString();
            }
            if (this.unread_count > 0) {
              this.unread_count--;
            }
          }
        }
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    },

    async mark_all_read() {
      const token = get_token();
      if (!token) return;
      try {
        const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/notification_management/mark_all_notifications_as_read/`;
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
            'X-Page-URL': window.location.href,
            ...csrf_headers(this.csrfToken),
          },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success') {
            this.$notyf?.success?.('All notifications marked as read');
            this.notifications.forEach(n => {
              n.status = 'READ';
              n.read_at = new Date().toISOString();
            });
            this.unread_count = 0;
          }
        }
      } catch (error) {
        console.error('Error marking all as read:', error);
        this.$notyf?.error?.('Failed to mark all as read');
      }
    },

    async dismiss_notification(notification_id) {
      Swal.fire({
        title: 'Dismiss Notification?',
        text: `Are you sure you want to dismiss this notification from your list?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, dismiss!',
        cancelButtonText: 'Cancel'
      }).then(async (result) => {
        if (!result.isConfirmed) return;
        const token = get_token();
        if (!token) return;
        try {
          const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/notification_management/dismiss_notification/`;
          const response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Authorization': `Token ${token}`,
              'Content-Type': 'application/json',
              'X-Page-URL': window.location.href,
              ...csrf_headers(this.csrfToken),
            },
            body: JSON.stringify({ notification_id }),
          });
          if (response.ok) {
            const data = await response.json();
            if (data.status === 'success') {
              const notif = this.notifications.find(n => n.id === notification_id);
              const was_unread = notif && notif.status !== 'READ';
              this.notifications = this.notifications.filter(n => n.id !== notification_id);
              if (was_unread && this.unread_count > 0) {
                this.unread_count--;
              }
            }
          }
        } catch (error) {
          console.error('Error dismissing notification:', error);
        }
      });
    },

    async dismiss_all() {
      Swal.fire({
          title: 'Clear All Notifications?',
          text: `Are you sure you want to clear all notifications?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, clear!',
          cancelButtonText: 'Cancel'
        }).then(async (result) => {
          if (!result.isConfirmed) return;
          const token = get_token();
          if (!token) return;
          try {
            const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/notification_management/dismiss_all_notifications/`;
            const response = await fetch(url, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Authorization': `Token ${token}`,
                'Content-Type': 'application/json',
                'X-Page-URL': window.location.href,
                ...csrf_headers(this.csrfToken),
              },
            });
            if (response.ok) {
              const data = await response.json();
              if (data.status === 'success') {
                this.$notyf?.success?.('All notifications cleared');
                this.notifications = [];
                this.unread_count = 0;
              }
            }
          } catch (error) {
            console.error('Error dismissing all notifications:', error);
            this.$notyf?.error?.('Failed to clear notifications');
          }
        });
    },

    handle_notification_click(notif) {
      if (notif.status !== 'READ') {
          this.mark_notification_read(notif.id);
      }
      const raw_url = notif.action_url;
      if (raw_url) {
         try {
            if (raw_url.startsWith('http')) {
                const url_obj = new URL(raw_url);

                if (url_obj.hostname === window.location.hostname) {
                    this.$router.push(url_obj.pathname + url_obj.search + url_obj.hash);
                }
            } else {
                this.$router.push(raw_url);
            }
        } catch (e) {
            console.error("URL parsing failed, trying direct push:", e);
            this.$router.push(raw_url);
        }
      } else {
          console.warn("No action_url found on this notification object.");
      }
    },

    open_notifications() {
      this.fetch_notifications(false);
    },

    start_notification_polling() {
      // Fetch both count and notifications every 30 seconds in the background
      this.notification_poll_interval = setInterval(() => {
        this.fetch_unread_count();
        this.fetch_notifications(false); // Silent background refresh
      }, 30000);
    },

    format_time_ago(timestamp) {
      if (!timestamp) return '';
      const now = new Date();
      const time = new Date(timestamp);
      const diff = Math.floor((now - time) / 1000);
      if (diff < 60) return 'Just now';
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
      return time.toLocaleDateString();
    },

    init_chat_socket() {
      const token = get_token();
      if (!token) return;

      if (this.chat_socket) {
        this.chat_socket.disconnect();
      }

      this.chat_socket = new OneChatWebSocketClient({
        token,
        on_open: () => {
          this.chat_socket_connected = true;
          this.chat_socket_last_error = null;
          this.fetch_unread_chat_count();
        },
        on_close: () => {
          this.chat_socket_connected = false;
        },
        on_error: (event) => {
          this.chat_socket_last_error = event;
          console.error('ONE Chat navbar websocket error', event);
        },
        on_event: event => this.handle_chat_socket_event(event),
      });
      this.chat_socket.connect();
    },

    handle_chat_socket_event(event) {
      if (event?.type !== 'unread.total.updated') return;
      this.apply_unread_chat_count(event.unread_count);
    },

    async fetch_unread_chat_count() {
      if (!this.chat_socket?.is_open()) return;

      try {
        const response = await this.chat_socket.request('unread.total');
        this.apply_unread_chat_count(response?.data?.unread_count);
      } catch (error) {
        console.error('Error fetching chat count over websocket:', error);
      }
    },

    apply_unread_chat_count(raw_count) {
      const new_count = Number(raw_count || 0);

      if (this._chat_count_initialized && new_count > this.unread_chat_count) {
        const diff = new_count - this.unread_chat_count;
        this.fire_browser_notification(
          'New Message - ONE Chat',
          diff === 1
            ? 'You have a new message'
            : `You have ${diff} new messages`,
          {
            tag: 'aic-chat-message',
            on_click: () => this.$router.push('/chat'),
          }
        );
      }

      this._chat_count_initialized = true;
      this.unread_chat_count = new_count;
    },

  },

  watch: {
    '$branding.logos.logo': {
      handler(new_logo) {
        if (new_logo) {
          this.logo_url = new_logo;
        }
      },
      deep: true
    },
    '$branding.logos.favicon': {
      handler(new_favicon) {
        if (new_favicon) {
          this.$branding.update_favicon(new_favicon);
        }
      },
      deep: true
    }
  }
};
